import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  SetStateAction,
  Dispatch,
  useRef,
} from 'react'
import cn from 'classnames'
import { format } from 'date-fns'
import {
  IUser,
  IDivision,
  IBranch,
  IAccount,
  ITeam,
  UserRole,
} from '../../types/user'
import { IHash } from '../../types/common'
import Input from '../../components/Input'
import Button from '../../components/Button'
import Toggle from '../../components/Toggle'
import Tooltip from '../../components/Tooltip'
import LockIcon from '../../icons/lock_filled.svg'
import UserChangePassword from './UserChangePassword'
import UserBranchMove from './UserBranchMove'
import DropdownSelect from '../../components/DropdownSelect'
import GroupsForm from './GroupsForm'
import InfoCircleIcon from '../../icons/info_circle.svg'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../auth/authSelectors'

export interface IOwnProps {
  user: IUser
  account?: IAccount
  divisions: IDivision[]
  teams: ITeam[]
  branch: IBranch
  loading: boolean
  errors: IHash<string>
  onSave: (user: IUser | { password?: string }) => Promise<void>
  onDelete?: (user: IUser) => void
  onCancel?: () => void
  onPasswordUpdate?: (user: IUser, password: string) => void
  onBranchUpdate?: (user: IUser, branch: IBranch) => void
}

export interface IUserInfoProps<T> extends IOwnProps {
  filter?: string
  isEditing?: boolean
  setIsEditing?: Dispatch<SetStateAction<boolean>>
}

interface IUserRolesOption {
  label: string
  value: string
}

export const USER_ROLES: IUserRolesOption[] = [
  { label: 'Manager', value: 'manager' },
  { label: 'General Manager', value: 'general_manager' },
  { label: 'Checkout Manager', value: 'checkout_manager' },
  {
    label: 'General and Checkout Manager',
    value: 'general_and_checkout_manager',
  },
  { label: 'Technician', value: 'technician' },
  { label: 'Planner', value: 'planner' },
]

export const USER_ROLES_TO_SELECT: IUserRolesOption[] = [
  { label: 'Choose Role', value: '' },
  { label: 'Manager', value: 'manager' },
  { label: 'Technician', value: 'technician' },
  { label: 'Planner', value: 'planner' },
]

export enum UserStatus {
  Active = 'active',
  Pending = 'invited',
  Inactive = 'inactive',
}

const getInitialUserRole = (user: IUser): UserRole => user.role || ''

const UserForm = ({
  user: initialUser,
  branch,
  account,
  loading,
  filter,
  errors,
  onSave,
  onDelete,
  onCancel,
  onPasswordUpdate,
  onBranchUpdate,
  divisions,
  teams,
}: IUserInfoProps<IOwnProps>) => {
  const [user, setUser] = useState<IUser>({ ...initialUser })
  const currentUser = useSelector(currentUserSelector)
  const [password, setPassword] = useState<string>('')
  const [userRole, setUserRole] = useState<UserRole>(
    getInitialUserRole(initialUser),
  )
  const nameInputRef = useRef<HTMLInputElement>(null)
  const currentAccount = account || currentUser.account

  useEffect(() => {
    nameInputRef.current?.focus()
  }, [])

  const [changePasswordVisible, setChangePasswordVisible] =
    useState<boolean>(false)
  const [moveVisible, setMoveVisible] = useState<boolean>(false)

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      setUser({ ...user, password })
      onSave({ ...user, password })
    },
    [user, password],
  )

  const handleDelete = useCallback(() => {
    if (user.isRemovable && onDelete) {
      const confirmMessage = `Are you sure you want to delete ${user.name}?`
      if (window.confirm(confirmMessage)) {
        onDelete(user)
      }
    }
  }, [user])

  const isNew = !user.id

  useEffect(() => {
    const selectedUserRole =
      isNew &&
      filter !== 'all' &&
      filter !== 'inactive' &&
      filter !== 'binUsers'
        ? filter === 'technicians'
          ? 'technician'
          : filter === 'generalManagers'
            ? 'manager'
            : filter === 'checkoutManagers'
              ? 'manager'
              : filter === 'managers'
                ? 'manager'
                : 'planner'
        : getInitialUserRole(initialUser)

    setUserRole(selectedUserRole as UserRole)
    setPassword('')

    const updatedUser: IUser = {
      ...initialUser,
      role: selectedUserRole,
      ...(isNew && {
        emailInboundInventoryApproval: true,
        emailLowStockWarning: true,
        emailOrderApprovalRequest: true,
        emailReplenishmentPlanReminder: true,
        emailLocationInventory: true,
      }),
    }

    setUser(updatedUser)
  }, [initialUser.id])

  const handleRoleChange = (selectedRole: string) => {
    const isCheckoutManager = [
      'checkout_manager',
      'general_and_checkout_manager',
    ].includes(selectedRole)
    const isGeneralManager = [
      'general_manager',
      'general_and_checkout_manager',
    ].includes(selectedRole)
    setUser({
      ...user,
      role: selectedRole as UserRole,
      checkoutManager: isCheckoutManager,
      generalManager: isGeneralManager,
    })
    setPassword('')
    setUserRole(selectedRole as UserRole)
  }

  return (
    <form className="user-form" onSubmit={handleSubmit}>
      <div className="user-form_info">
        <div className={cn('user-form_field', { '-error': errors.name })}>
          <Input
            label="name"
            ref={nameInputRef}
            error={errors.name}
            value={user.name}
            onChange={(e) => setUser({ ...user, name: e.target.value })}
          />
        </div>

        <div className={cn('user-form_field', { '-error': errors.email })}>
          <Input
            label="email"
            error={errors.email}
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
          />
        </div>

        <div className={cn('user-form_field', { '-error': errors.role })}>
          <DropdownSelect<string | undefined>
            label="Role"
            value={userRole}
            options={USER_ROLES_TO_SELECT as any}
            onChange={handleRoleChange}
            error={errors.role}
            className="-md"
            dropdownClassName="input"
            dropdownValueClassName="input_input -flex white-bg"
          />
        </div>

        {isNew && (
          <div className={cn('user-form_field', { '-error': errors.password })}>
            <Input
              label="password"
              placeholder="5+ symbols"
              error={errors.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}

        {!isNew && user.status !== UserStatus.Pending && (
          <div className="user-form_box -slim">
            <Toggle
              value={user.status === UserStatus.Active}
              onChange={() =>
                setUser({
                  ...user,
                  status:
                    user.status === UserStatus.Active
                      ? UserStatus.Inactive
                      : UserStatus.Active,
                })
              }
              option={{
                value: true,
                label: 'Active User',
              }}
            />
          </div>
        )}
        {user.role !== 'technician' && user.role !== 'planner' && (
          <>
            {currentAccount && currentAccount.orderApproveFlow && (
              <>
                <div className="user-form_box -slim">
                  <div className="user-form_text -inline">
                    <Toggle
                      value={user.generalManager}
                      onChange={() =>
                        setUser({
                          ...user,
                          generalManager: !user.generalManager,
                        })
                      }
                      option={{
                        value: true,
                        label: 'General Manager',
                      }}
                    />
                  </div>
                  <div className="user-form_text">
                    <Tooltip id={`tooltip-checkout-manager`}>
                      <div
                        className="input_label-tooltip"
                        data-tip="
                      <div>
                        The user will be able to approve orders so that they can be sent to Forshaw
                      </div>
                    "
                        data-for={`tooltip-checkout-manager`}
                        data-effect="solid"
                        data-place="top"
                        style={{ marginTop: '5px' }}
                        data-html
                      >
                        <InfoCircleIcon />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {user.role !== 'planner' && (
          <>
            <div className="user-form_box -slim">
              <div className="user-form_text -inline">
                <Toggle
                  value={user.checkoutManager}
                  onChange={() =>
                    setUser({ ...user, checkoutManager: !user.checkoutManager })
                  }
                  option={{
                    value: true,
                    label: 'Manage inventory checkout requests',
                  }}
                />
              </div>
              <div className="user-form_text">
                <Tooltip id={`tooltip-checkout-manager`}>
                  <div
                    className="input_label-tooltip"
                    data-tip="
                    <div>
                      The user will be able to manage inventory checkout requests:
                      <br />
                      <ul>
                        <li>Receive notifications about new requests</li>
                        <li>Accept or decline incoming requests</li>
                        <li>Create new requests for other users</li>
                      </ul>
                    </div>
                  "
                    data-for={`tooltip-checkout-manager`}
                    data-effect="solid"
                    data-place="top"
                    style={{ marginTop: '5px' }}
                    data-html
                  >
                    <InfoCircleIcon />
                  </div>
                </Tooltip>
              </div>
            </div>
          </>
        )}
        {branch?.binInventoryFeature && currentAccount?.binInventoryFeature && (
          <div className="user-form_box -slim">
            <div className="user-form_text -inline">
              <Toggle
                value={user.binCount}
                onChange={() => setUser({ ...user, binCount: !user.binCount })}
                option={{
                  value: true,
                  label: 'Bin Count',
                }}
              />
            </div>
            <div className="user-form_text">
              <Tooltip id={`tooltip-checkout-manager`}>
                <div
                  className="input_label-tooltip"
                  data-tip="Enables control over bin inventory management for this branch"
                  data-for={`tooltip-checkout-manager`}
                  data-effect="solid"
                  data-place="top"
                  style={{ marginTop: '5px' }}
                  data-html
                >
                  <InfoCircleIcon />
                </div>
              </Tooltip>
            </div>
          </div>
        )}
        <>
          {(user.role === 'manager' || user.role === 'planner') && (
            <div className="user-form_full_width_border"></div>
          )}
          {user.role === 'manager' && (
            <>
              <div className="user-form_box -slim">
                <div className="user-form_text -inline">
                  <Toggle
                    value={user.emailInboundInventoryApproval}
                    onChange={() =>
                      setUser({
                        ...user,
                        emailInboundInventoryApproval:
                          !user.emailInboundInventoryApproval,
                      })
                    }
                    option={{
                      value: true,
                      label: 'Email inbound inventory approval request',
                    }}
                  />
                </div>
              </div>
              <div className="user-form_box -slim">
                <div className="user-form_text -inline">
                  <Toggle
                    value={user.emailLowStockWarning}
                    onChange={() =>
                      setUser({
                        ...user,
                        emailLowStockWarning: !user.emailLowStockWarning,
                      })
                    }
                    option={{
                      value: true,
                      label: 'Email low stock warning',
                    }}
                  />
                </div>
              </div>
              {branch.truckInventoryFeature && (
                <div className="user-form_box -slim">
                  <div className="user-form_text -inline">
                    <Toggle
                      value={user.emailLocationInventory}
                      onChange={() =>
                        setUser({
                          ...user,
                          emailLocationInventory: !user.emailLocationInventory,
                        })
                      }
                      option={{
                        value: true,
                        label: 'Email truck inventory notifications',
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {user.role === 'manager' && user.generalManager && (
            <div className="user-form_box -slim">
              <div className="user-form_text -inline">
                <Toggle
                  value={user.emailOrderApprovalRequest}
                  onChange={() =>
                    setUser({
                      ...user,
                      emailOrderApprovalRequest:
                        !user.emailOrderApprovalRequest,
                    })
                  }
                  option={{
                    value: true,
                    label: 'Email order approval request',
                  }}
                />
              </div>
            </div>
          )}

          {user.role === 'manager' && (
            <>
              <div className="user-form_box -slim">
                <div className="user-form_text -inline">
                  <Toggle
                    value={user.emailReplenishmentPlanReminder}
                    onChange={() =>
                      setUser({
                        ...user,
                        emailReplenishmentPlanReminder:
                          !user.emailReplenishmentPlanReminder,
                      })
                    }
                    option={{
                      value: true,
                      label: 'Email replenishment plan reminder',
                    }}
                  />
                </div>
              </div>
              <div className="user-form_box -slim">
                <div className="user-form_text -inline">
                  <Toggle
                    value={user.enableReports}
                    onChange={() =>
                      setUser({
                        ...user,
                        enableReports: !user.enableReports,
                      })
                    }
                    option={{
                      value: true,
                      label: 'Enable reports',
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {user.role === 'planner' && (
            <div className="user-form_box -slim">
              <div className="user-form_text -inline">
                <Toggle
                  value={user.emailReplenishmentPlanReminder}
                  onChange={() =>
                    setUser({
                      ...user,
                      emailReplenishmentPlanReminder:
                        !user.emailReplenishmentPlanReminder,
                    })
                  }
                  option={{
                    value: true,
                    label: 'Email replenishment plan reminder',
                  }}
                />
              </div>
            </div>
          )}
        </>
        {!isNew && <div className="user-form_full_width_border"></div>}
        {!isNew && user.role === 'technician' && (
          <>
            <div className="user-form_box">
              <div className="user-form_text -inline">At {branch.name}</div>
              <Tooltip id="user-form-move">
                <div
                  className={cn('user-form_text -primary -link', {
                    '-disabled': !user.isMovable,
                  })}
                  data-tip={
                    !user.isMovable
                      ? 'You need to complete all user requests in mobile app to move user to another branch'
                      : null
                  }
                  data-for="user-form-move"
                  data-effect="solid"
                  data-place="top"
                  onClick={() => user.isMovable && setMoveVisible(true)}
                >
                  Move to another branch
                </div>
              </Tooltip>
            </div>
          </>
        )}
        {!isNew && (
          <div className="user-form_box">
            <div className="user-form_text">
              {user.lastSignInAt
                ? `Last sign in ${format(user.lastSignInAt, 'MM/DD hh:mm A')}`
                : 'Never signed in'}
            </div>
            <div
              className="user-form_text -danger -link"
              onClick={() => setChangePasswordVisible(true)}
            >
              Change password
            </div>
          </div>
        )}
      </div>

      <div className="user-form_full_width_border"></div>

      <GroupsForm
        user={user}
        setUser={setUser}
        loading={loading}
        errors={errors}
        divisions={divisions}
        teams={teams}
      />

      <div className="user-form_footer">
        {!isNew && (
          <Tooltip id="user-form-delete">
            <div
              className={`user-form_delete ${!user.isRemovable ? '-disabled' : ''}`}
              onClick={handleDelete}
              data-tip={
                !user.isRemovable
                  ? 'This user can’t be deleted but you can deactivate the user'
                  : null
              }
              data-for="user-form-delete"
              data-effect="solid"
              data-place="top"
            >
              {!user.isRemovable && <LockIcon className="user-form_icon" />}
              Delete
            </div>
          </Tooltip>
        )}
        <div className="user-form_actions-right">
          <div className="user-form_cancel" onClick={onCancel}>
            Cancel
          </div>
          <Button
            color="success"
            type="submit"
            loading={loading}
            disabled={loading}
          >
            {'Save'}
          </Button>
        </div>
      </div>

      <UserChangePassword
        opened={changePasswordVisible}
        onUpdate={(password) =>
          onPasswordUpdate && onPasswordUpdate(user, password)
        }
        onClose={() => setChangePasswordVisible(false)}
      />

      <UserBranchMove
        branch={branch}
        opened={moveVisible}
        onUpdate={(branch: IBranch) =>
          onBranchUpdate && onBranchUpdate(user, branch)
        }
        onClose={() => setMoveVisible(false)}
      />
    </form>
  )
}

export default memo(UserForm)
